import type { GroupedFieldsBlockField, TextBlockField } from './descriptor';
import {
  EMPTY_UNIT_VALUE,
  normalizeUnitValue,
  serializeUnitValue,
  type UnitValue,
  type UnitValueChoice,
} from './unit-value';

/** Sides of a CSS margin/padding box, in clockwise order from top. */
export const SPACING_BOX_SIDES = ['top', 'right', 'bottom', 'left'] as const;

export type SpacingBoxSide = (typeof SPACING_BOX_SIDES)[number];

/** Runtime / persisted value for the `spacing_box` property type. */
export type SpacingBoxValue = {
  top: UnitValue;
  right: UnitValue;
  bottom: UnitValue;
  left: UnitValue;
};

export const EMPTY_SPACING_BOX: SpacingBoxValue = {
  top: { ...EMPTY_UNIT_VALUE },
  right: { ...EMPTY_UNIT_VALUE },
  bottom: { ...EMPTY_UNIT_VALUE },
  left: { ...EMPTY_UNIT_VALUE },
};

export function normalizeSpacingBox(value: unknown): SpacingBoxValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      top: { ...EMPTY_UNIT_VALUE },
      right: { ...EMPTY_UNIT_VALUE },
      bottom: { ...EMPTY_UNIT_VALUE },
      left: { ...EMPTY_UNIT_VALUE },
    };
  }
  const record = value as Record<string, unknown>;
  return {
    top: normalizeUnitValue(record['top']),
    right: normalizeUnitValue(record['right']),
    bottom: normalizeUnitValue(record['bottom']),
    left: normalizeUnitValue(record['left']),
  };
}

/** Serializes one side to a CSS length (`auto`, `16px`, `1rem`, …). */
export function serializeSpacingBoxSide(
  value: unknown,
  choices?: readonly UnitValueChoice[],
): string {
  return serializeUnitValue(value, choices);
}

/** Structural input — avoids invariant `PropertyValue<T>` assignability issues. */
export type SpacingBoxPropertyInput = {
  readonly value?: unknown;
  readonly options?: Record<string, unknown>;
};

/**
 * CB1 projection: a single `spacing_box` leaf becomes a `groupedFields` of four
 * text inputs (serialized unit values) so legacy editors keep working.
 */
export function expandSpacingBoxToGroupedFields(
  handle: string,
  propertyValue: SpacingBoxPropertyInput,
): GroupedFieldsBlockField {
  const box = normalizeSpacingBox(propertyValue.value);
  const options = propertyValue.options ?? {};
  const groupLabel =
    typeof options['label'] === 'string' ? options['label'] : handle;
  const sideLabelPrefix =
    typeof options['sideLabelPrefix'] === 'string'
      ? options['sideLabelPrefix']
      : undefined;
  const choices = Array.isArray(options['choices'])
    ? (options['choices'] as UnitValueChoice[])
    : undefined;

  const fields: TextBlockField[] = SPACING_BOX_SIDES.map(side => ({
    handle: side,
    type: 'text' as const,
    options: {
      label: sideLabelPrefix ? `${sideLabelPrefix}.${side}.label` : side,
      default: serializeSpacingBoxSide(box[side], choices),
    },
  }));

  return {
    handle,
    type: 'groupedFields',
    options: {
      label: groupLabel,
      containerType: 'fieldset',
      fields,
    },
  };
}
